import api, { clearCache } from './apiClient';

export interface CompanyRelationshipUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  cedula?: string;
  role?: string;
}

export interface RequestedUser {
  name: string;
  cedula: string;
  status: 'pending' | 'approved' | 'rejected' | 'substituted';
  matchedUser?: { _id: string; firstName: string; lastName: string; email: string; cedula?: string } | string;
  substituteUser?: { _id: string; firstName: string; lastName: string; email: string; cedula?: string } | string;
  substituteName?: string;
  rejectionReason?: string;
  respondedAt?: string;
}

export interface PendingAssignment {
  user: { _id: string; firstName: string; lastName: string; email: string; cedula?: string } | string;
  selectedBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  status: 'pending' | 'confirmed' | 'rejected';
  rejectionReason?: string;
  decidedAt?: string;
  createdAt: string;
}

// Reverse direction of PendingAssignment: the PROVIDER picks the user and
// the CLIENT gives final approval — see requestUserForClient/
// respondToProviderRequest and getRelationshipAvailableSeats on the BE.
export interface ProviderInitiatedRequest {
  user: { _id: string; firstName: string; lastName: string; email: string; cedula?: string } | string;
  requestedBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  decidedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  decidedAt?: string;
  createdAt: string;
}

export interface AvailableClientForProvider {
  relationshipId: string;
  clientCompanyId: string;
  clientCompanyName: string;
  userAllocation: number;
  availableSeats: number;
}

export interface CompanyRelationship {
  _id: string;
  clientCompany: { _id: string; name: string; companyType: string } | string;
  proveedorCompany: { _id: string; name: string; companyType: string } | string;
  status: 'active' | 'inactive' | 'pending' | 'esperando_proveedor' | 'esperando_cliente' | 'edicion_solicitada' | 'usuarios_asignados';
  startDate?: string;
  endDate?: string;
  notes?: string;
  editNotes?: string;
  invitedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  /** Null when created with sendInvitation: false — the provider hasn't
   *  been notified yet. Set once send-invitation fires (at creation or
   *  later via companyRelationshipApi.sendInvitation). */
  invitationSentAt?: string | null;
  respondedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  respondedAt?: string;
  requiredVerifications?: string[];
  availableUsers?: string[];
  requestedUsers?: RequestedUser[];
  /** Set by the CLIENT — lets this provider create Fichas de Servicio,
   *  Evaluaciones Iniciales, and Permisos de Trabajo in their own company
   *  without a client association. Defaults to false/unset. */
  allowProviderSelfService?: boolean;
  /** Client-controlled: can this provider add users at all, and how many
   *  (sub-budget of the client's plan-wide provider-user cap). */
  canAddUsers?: boolean;
  userAllocation?: number;
  pendingAssignments?: PendingAssignment[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Conteos de verificaciones de la empresa PROVEEDORA propia del caller,
 * agregados sobre todas sus empresas cliente vinculadas — alimenta el panel
 * del Inicio del admin de proveedora. Separa documentos DE LA EMPRESA
 * (nivel "company", uno por relación) de los de SU PERSONAL (uno por
 * trabajador), porque se resuelven en pantallas distintas.
 */
export interface ProviderVerificationSummary {
  providerCompany: { _id: string; name: string };
  clientCount: number;
  companyDocuments: { rejected: number; pending: number; missing: number };
  staffDocuments: { rejected: number; pending: number };
  /** Los rechazados (máx. 6) — lo único que exige acción inmediata. */
  rejected: Array<{
    kind: 'company' | 'staff';
    name: string;
    clientId: string;
    clientName: string;
    personName?: string;
    reason: string;
  }>;
  rejectedTotal: number;
}

export interface ProviderSelfServiceStatus {
  canSelfServe: boolean;
  /** Does this provider have its own active Subscription? Unlike
   *  canSelfServe (which any linked client can also grant via
   *  allowProviderSelfService), this is the ONLY thing that unlocks
   *  company-management actions — adding external staff, subcontracting
   *  another provider, hosting visitas — see providerSelfServiceGate.ts
   *  on the BE (providerHasOwnActivePlan). */
  hasOwnActivePlan: boolean;
  /** Can this provider create another user in its OWN company right now?
   *  A provider with no plan of its own is gated on being linked to at
   *  least one client — see getProviderOwnCompanyUserStatus on the BE. */
  userCreation: {
    blocked: boolean;
    reason?: string;
    limit: number;
    currentUsers: number;
    /** Per-client contribution to `limit` — one entry per linked
     *  relationship that grants a seat allocation. */
    breakdown: Array<{
      relationshipId: string;
      clientCompanyId: string;
      clientCompanyName: string;
      userAllocation: number;
    }>;
  };
  /** The provider's assigned Soporte Monitor, to show as a contact point
   *  for buying a plan — null if none assigned yet. */
  accountExecutive: {
    name: string;
    email?: string;
    phone?: string;
  } | null;
}

export const companyRelationshipApi = {
  invite: async (data: {
    providerCompanyId: string;
    clientCompanyId: string;
    requiredVerifications?: string[];
    requestedUsers?: Array<{ name: string; cedula: string }>;
    /** Defaults to true. Set false to create the relationship/request
     *  without notifying the provider yet — send it later with
     *  sendInvitation(). */
    sendInvitation?: boolean;
  }): Promise<CompanyRelationship> => {
    const res = await api.post<{ data: CompanyRelationship }>('/company-relationships/invite', data);
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  sendInvitation: async (relationshipId: string): Promise<CompanyRelationship> => {
    const res = await api.post<{ data: CompanyRelationship }>(`/company-relationships/${relationshipId}/send-invitation`, {});
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  getPending: async (): Promise<CompanyRelationship[]> => {
    const res = await api.get<{ data: CompanyRelationship[] }>('/company-relationships/pending');
    return res.data as CompanyRelationship[];
  },

  getForCompany: async (companyId: string, status?: string): Promise<CompanyRelationship[]> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const res = await api.get<{ data: CompanyRelationship[] }>(`/company-relationships/company/${companyId}`, params);
    return res.data as CompanyRelationship[];
  },

  respond: async (id: string, action: 'accept' | 'reject'): Promise<CompanyRelationship> => {
    const res = await api.patch<{ data: CompanyRelationship }>(`/company-relationships/${id}/respond`, { action });
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  requestEdit: async (id: string, editNotes: string): Promise<CompanyRelationship> => {
    const res = await api.patch<{ data: CompanyRelationship }>(`/company-relationships/${id}/request-edit`, { editNotes });
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  respondToUser: async (
    id: string,
    index: number,
    action: 'approve' | 'reject' | 'substitute',
    options?: { substituteUserId?: string; rejectionReason?: string },
  ): Promise<CompanyRelationship> => {
    const res = await api.patch<{ data: CompanyRelationship }>(`/company-relationships/${id}/respond-user`, {
      index,
      action,
      ...options,
    });
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  // Edit a still-pending requested person's name/cédula — locked once the
  // provider has responded to them (status leaves "pending").
  editRequestedUser: async (
    id: string,
    index: number,
    updates: { name?: string; cedula?: string },
  ): Promise<{ name: string; cedula: string; status: string }> => {
    const res = await api.patch<{ data: { name: string; cedula: string; status: string } }>(
      `/company-relationships/${id}/requested-users/${index}`,
      updates,
    );
    clearCache('/companies');
    return res.data as { name: string; cedula: string; status: string };
  },

  getAvailableUsers: async (id: string): Promise<CompanyRelationshipUser[]> => {
    const res = await api.get<{ data: CompanyRelationshipUser[] }>(`/company-relationships/${id}/available-users`);
    return res.data as CompanyRelationshipUser[];
  },

  updateAvailableUsers: async (id: string, userIds: string[]): Promise<CompanyRelationship> => {
    const res = await api.patch<{ data: CompanyRelationship }>(`/company-relationships/${id}/available-users`, { userIds });
    clearCache('/companies');
    return res.data as CompanyRelationship;
  },

  // Reserves a slot per userId against the relationship's userAllocation
  // and creates a pending assignment for each — does NOT link them in yet.
  // The provider must confirm each one via respondToPendingAssignment.
  assignUsers: async (id: string, userIds: string[]): Promise<PendingAssignment[]> => {
    const res = await api.post<{ data: PendingAssignment[] }>(`/company-relationships/${id}/assign-users`, { userIds });
    clearCache('/companies');
    return res.data as PendingAssignment[];
  },

  getPendingAssignments: async (id: string): Promise<PendingAssignment[]> => {
    const res = await api.get<{ data: PendingAssignment[] }>(`/company-relationships/${id}/pending-assignments`);
    return res.data as PendingAssignment[];
  },

  // The PROVIDER confirms or rejects one pending assignment by its array
  // index. Confirming is what actually links the user into the client's
  // roster; rejecting requires a reason and leaves the user selectable again.
  respondToPendingAssignment: async (
    id: string,
    index: number,
    action: 'confirm' | 'reject',
    rejectionReason?: string,
  ): Promise<PendingAssignment> => {
    const res = await api.patch<{ data: PendingAssignment }>(
      `/company-relationships/${id}/pending-assignments/${index}`,
      { action, rejectionReason },
    );
    clearCache('/companies');
    return res.data as PendingAssignment;
  },

  // Linked clients of the caller's own PROVEEDOR company, with the seats
  // each one has left — for the "asignar a cliente" dropdown when creating
  // or inviting a user in the provider's own company.
  // companyId: required for a platform role (super_admin/safety_staff)
  // managing another company's users — the BE resolves the caller's own
  // provider company otherwise, which platform roles don't have.
  getAvailableClientsForProvider: async (companyId?: string): Promise<AvailableClientForProvider[]> => {
    const res = await api.get<{ data: AvailableClientForProvider[] }>(
      '/company-relationships/available-for-provider',
      companyId ? { companyId } : undefined,
    );
    return res.data as AvailableClientForProvider[];
  },

  // The PROVIDER requests that one of its own users (new or existing, not
  // yet linked to this client) count against this relationship's seat
  // budget — reserved immediately, pending the client's final approval.
  requestUserForClient: async (
    relationshipId: string,
    userId: string,
  ): Promise<ProviderInitiatedRequest> => {
    const res = await api.post<{ data: ProviderInitiatedRequest }>(
      `/company-relationships/${relationshipId}/request-user`,
      { userId },
    );
    clearCache('/companies');
    return res.data as ProviderInitiatedRequest;
  },

  getProviderInitiatedRequests: async (
    relationshipId: string,
  ): Promise<ProviderInitiatedRequest[]> => {
    const res = await api.get<{ data: ProviderInitiatedRequest[] }>(
      `/company-relationships/${relationshipId}/provider-requests`,
    );
    return res.data as ProviderInitiatedRequest[];
  },

  // The CLIENT approves or rejects a provider-initiated request by its
  // array index. Approving is what actually links the user into the
  // client's roster; rejecting requires a reason and frees the seat.
  respondToProviderRequest: async (
    relationshipId: string,
    index: number,
    action: 'approve' | 'reject',
    rejectionReason?: string,
  ): Promise<ProviderInitiatedRequest> => {
    const res = await api.post<{ data: ProviderInitiatedRequest }>(
      `/company-relationships/${relationshipId}/provider-requests/${index}/respond`,
      { action, rejectionReason },
    );
    clearCache('/companies');
    return res.data as ProviderInitiatedRequest;
  },

  // The CLIENT sets, per provider, whether it can add users at all and how
  // many at most (sub-budget of the client's plan-wide provider-user cap).
  setProviderAllocation: async (
    id: string,
    data: { canAddUsers?: boolean; userAllocation?: number },
  ): Promise<{ canAddUsers: boolean; userAllocation: number }> => {
    const res = await api.patch<{ data: { canAddUsers: boolean; userAllocation: number } }>(
      `/company-relationships/${id}/user-allocation`,
      data,
    );
    clearCache('/companies');
    return res.data as { canAddUsers: boolean; userAllocation: number };
  },

  // super_admin / assigned Soporte Monitor only — splits `total` evenly
  // across every provider linked to this client in one atomic write.
  splitAllocationEvenly: async (
    clientCompanyId: string,
    total: number,
  ): Promise<Array<{ relationshipId: string; providerName: string; userAllocation: number }>> => {
    const res = await api.patch<{ data: Array<{ relationshipId: string; providerName: string; userAllocation: number }> }>(
      `/company-relationships/company/${clientCompanyId}/split-allocation`,
      { total },
    );
    clearCache('/companies');
    return res.data as Array<{ relationshipId: string; providerName: string; userAllocation: number }>;
  },

  // The CLIENT grants/revokes this provider's permission to create Fichas,
  // Evaluaciones, and Permisos in their own company.
  setSelfServicePermission: async (id: string, allow: boolean): Promise<{ allowProviderSelfService: boolean }> => {
    const res = await api.patch<{ data: { allowProviderSelfService: boolean } }>(
      `/company-relationships/${id}/self-service-permission`,
      { allow },
    );
    clearCache('/companies');
    return res.data as { allowProviderSelfService: boolean };
  },

  // PROVIDER's own status: can it create Fichas/Evaluaciones/Permisos in
  // its own company (granted by at least one linked client), and how much
  // user-allocation budget remains across its linked clients. For a
  // company_admin caller, the BE always resolves from the caller's own
  // active company and ignores `companyId` — pass it only so platform
  // staff (super_admin/safety_staff) can query an arbitrary provider's
  // status from ProviderCompanyDetail.tsx, which the BE requires from them.
  // Conteos de verificaciones pendientes/rechazadas de la empresa
  // proveedora propia del caller. El BE la resuelve desde la membresía
  // company_admin no-externa, así que funciona igual estando parado dentro
  // de una empresa cliente.
  getMyVerificationSummary: async (): Promise<ProviderVerificationSummary> => {
    const res = await api.get<{ data: ProviderVerificationSummary }>(
      '/company-relationships/my-provider-verification-summary',
    );
    return res.data;
  },

  getSelfServiceStatus: async (companyId?: string): Promise<ProviderSelfServiceStatus> => {
    const res = await api.get<{ data: ProviderSelfServiceStatus }>(
      '/company-relationships/provider-self-service-status',
      companyId ? { companyId } : undefined,
    );
    return res.data;
  },

  cancelInvitation: async (id: string): Promise<void> => {
    await api.delete(`/company-relationships/${id}/cancel`);
    clearCache('/companies');
  },

  // Verificaciones requeridas por el cliente para esta relación, subidas
  // por el company_admin de origen del proveedor en representación de
  // toda la empresa (ver relationshipVerificationController en el backend).
  getVerifications: async (relationshipId: string): Promise<RelationshipVerificationList> => {
    const res = await api.get<{ data: RelationshipVerification[]; isCompliant: boolean; blockUnverifiedAccess: boolean }>(
      `/company-relationships/${relationshipId}/verifications`,
    );
    return {
      items: res.data,
      isCompliant: res.isCompliant,
      blockUnverifiedAccess: res.blockUnverifiedAccess,
    };
  },

  // Configuración del cliente: universo de reglas elegibles para este
  // proveedor, cuáles están seleccionadas hoy, y si el acceso de usuarios
  // externos está bloqueado mientras la relación no se cumpla.
  getVerificationSettings: async (relationshipId: string): Promise<RelationshipVerificationSettings> => {
    const res = await api.get<RelationshipVerificationSettings>(
      `/company-relationships/${relationshipId}/verification-settings`,
    );
    return res;
  },

  updateVerificationSettings: async (
    relationshipId: string,
    data: { requiredVerifications: string[]; blockUnverifiedAccess: boolean },
  ): Promise<{ selectedRuleIds: string[]; blockUnverifiedAccess: boolean; isCompliant: boolean }> => {
    const res = await api.patch<{ selectedRuleIds: string[]; blockUnverifiedAccess: boolean; isCompliant: boolean }>(
      `/company-relationships/${relationshipId}/verification-settings`,
      data,
    );
    clearCache('/company-relationships');
    return res;
  },

  // Sums/removes ONE rule from requiredVerifications without reading and
  // rewriting the whole array (updateVerificationSettings above does that,
  // which is fine for the "Configurar verificaciones" checkbox picker but
  // risks silently dropping an already-required rule that fell out of
  // eligibility for unrelated reasons — see setRelationshipRequiredVerification
  // on the BE). Prefer this for "add/remove just this one rule" call sites.
  setRequiredVerification: async (
    relationshipId: string,
    verificationId: string,
    required: boolean,
  ): Promise<{ required: boolean; isCompliant: boolean }> => {
    const res = await api.patch<{ required: boolean; isCompliant: boolean }>(
      `/company-relationships/${relationshipId}/required-verifications/${verificationId}`,
      { required },
    );
    clearCache('/company-relationships');
    return res;
  },

  submitVerification: async (
    relationshipId: string,
    companyVerificationId: string,
    data: { fileKey: string; fileName: string; fileMimeType: string; fileSize: number; notes?: string },
  ): Promise<RelationshipVerification> => {
    const res = await api.post<{ data: RelationshipVerification }>(
      `/company-relationships/${relationshipId}/verifications/${companyVerificationId}/submit`,
      data,
    );
    clearCache('/company-relationships');
    return res.data as RelationshipVerification;
  },

  // Aprobar/rechazar un envío de NIVEL EMPRESA (verificationLevel==='company').
  // Las filas de nivel 'individual' se siguen revisando vía
  // userVerificationApi.reviewCompanyVerification, sin cambios.
  reviewVerification: async (
    relationshipId: string,
    companyVerificationId: string,
    data: { action: 'approve' | 'reject'; rejectionReason?: string; expiryDate?: string },
  ): Promise<RelationshipVerification> => {
    const res = await api.post<{ data: RelationshipVerification }>(
      `/company-relationships/${relationshipId}/verifications/${companyVerificationId}/review`,
      data,
    );
    clearCache('/company-relationships');
    return res.data as RelationshipVerification;
  },

  getDocumentDownloadUrl: async (
    relationshipId: string,
    companyVerificationId: string,
  ): Promise<{ url: string; expiresIn: number; fileName: string; mimeType: string }> => {
    const res = await api.get<{ data: { url: string; expiresIn: number; fileName: string; mimeType: string } }>(
      `/company-relationships/${relationshipId}/verifications/${companyVerificationId}/download-url`,
    );
    return res.data;
  },

  // Whether the session's active company should be restricted to the
  // exclusive submit/review screen — see Layout.tsx + RestrictedProviderAccessScreen.
  getMyRestrictedAccess: async (): Promise<RestrictedAccessInfo> => {
    const res = await api.get<{ data: RestrictedAccessInfo }>(
      '/company-relationships/my-restricted-access',
    );
    return res.data;
  },

  // Agregado de verificaciones de TODOS los proveedores vinculados a una
  // empresa cliente — alimenta la pestaña "De Cliente" del dashboard de
  // verificaciones (gráfico + lista de pendientes accionable).
  // companyId omitted = global mode (super_admin/safety_staff only) —
  // aggregates nivel-empresa items across every client company.
  getClientProviderVerifications: async (
    companyId?: string,
    statuses?: string,
  ): Promise<ClientProviderVerificationItem[]> => {
    const params: Record<string, string> = {};
    if (companyId) params.companyId = companyId;
    if (statuses) params.status = statuses;
    const res = await api.get<{ data: ClientProviderVerificationItem[] }>(
      '/company-relationships/client-verifications',
      params,
    );
    return res.data as ClientProviderVerificationItem[];
  },

  // Whether the session's active company should be entirely blocked
  // because it's the caller's own (non-external) provider company and it
  // has no active plan — see Layout.tsx + ProviderPlanBlockedScreen.
  getMyProviderPlanBlock: async (): Promise<ProviderPlanBlockInfo> => {
    const res = await api.get<{ data: ProviderPlanBlockInfo }>(
      '/company-relationships/my-provider-plan-block',
    );
    return res.data;
  },
};

export interface RestrictedAccessInfo {
  restricted: boolean;
  relationshipId?: string;
  providerCompanyId?: string;
}

export interface ProviderPlanBlockInfo {
  blocked: boolean;
  companyName?: string;
}

export interface RelationshipVerification {
  companyVerificationId: string;
  name: string;
  description?: string;
  type: string;
  isRequired: boolean;
  /** 'individual' = per-worker document (legacy default); 'company' = a
   *  single whole-company document, never an individual's personal to-do. */
  verificationLevel: 'individual' | 'company';
  /** Days an approved submission stays valid before needing renewal. Only
   *  meaningful together with requiresRenewal — otherwise it's a one-time
   *  check with no recurring expiry. */
  validityPeriod: number | null;
  requiresRenewal: boolean;
  status: 'not_submitted' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired';
  submissionId: string | null;
  fileName: string | null;
  submittedDate: string | null;
  approvedDate: string | null;
  rejectionReason: string | null;
  expiryDate: string | null;
}

export interface RelationshipVerificationList {
  items: RelationshipVerification[];
  /** True when every required rule has an approved submission (or there are
   *  none required). Mirrors the BE's isRelationshipCompliant. */
  isCompliant: boolean;
  /** Whether the client has enabled the access block for this relationship
   *  — when true and !isCompliant, the provider's external staff (other
   *  than its own home company_admin) can't log in for this company. */
  blockUnverifiedAccess: boolean;
}

export interface EligibleVerificationRule {
  id: string;
  name: string;
  description?: string;
  type: string;
  verificationLevel: 'individual' | 'company';
}

export interface RelationshipVerificationSettings {
  availableRules: EligibleVerificationRule[];
  selectedRuleIds: string[];
  blockUnverifiedAccess: boolean;
  isCompliant: boolean;
}

/**
 * One row of the "De Empresa" aggregation across every linked provider —
 * NIVEL EMPRESA only (whole-company documents, CompanyRelationship.
 * requiredVerifications). Individual-level provider verifications (per
 * worker, own or lent-in) belong to "De Cliente" instead and are read via
 * the existing GET /user-verifications/pending — see
 * clientProviderVerificationsService on the BE for why the split is by
 * Nivel, not by internal/provider audience.
 */
export interface ClientProviderVerificationItem {
  relationshipId: string;
  clientCompanyId: string;
  clientCompanyName: string;
  providerCompanyId: string;
  providerCompanyName: string;
  companyVerificationId: string;
  name: string;
  type: string;
  isRequired: boolean;
  verificationLevel: 'company';
  status: 'not_submitted' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired';
  submittedDate: string | null;
  approvedDate: string | null;
  rejectionReason: string | null;
  fileName: string | null;
  submissionId: string | null;
  submitterName: string | null;
}
